/** Social framing around the exact canonical collector face. No independent card drawing. */
export type ShareCardFormat = 'story' | 'portrait' | 'square';

export interface ShareCardStat {
  label: string;
  value: string;
}

export type ShareCardHighlightKind = 'achievement' | 'award' | 'dyno' | 'personal_best';

export interface ShareCardHighlight {
  kind: ShareCardHighlightKind;
  /** Main line, e.g. achievement name, award title, or dyno result. */
  title: string;
  /** Supporting context, e.g. "Ranked #3 of 412" or venue and date. */
  detail?: string | null;
}

export interface ShareCardOptions {
  format: ShareCardFormat;
  /** Permission-filtered source model; share options never change the physical card. */
  card: import('../services/api/passportCards').PassportCard;
  /** Primary vehicle title, e.g. "2004 Subaru WRX STI". */
  title: string;
  /** Secondary line, e.g. a nickname. */
  subtitle?: string | null;
  ownerName?: string | null;
  /** Up to 4 stats are rendered; extras are ignored. */
  stats?: ShareCardStat[];
  /**
   * Featured recognition rows (achievements, awards, dyno, PBs).
   * Prefer this over `highlight`. Max 3 drawn.
   */
  highlights?: ShareCardHighlight[] | null;
  /** @deprecated Prefer `highlights`. Single featured callout. */
  highlight?: ShareCardHighlight | null;
  /** Optional invested/price line, e.g. "$12,400 invested". */
  investedText?: string | null;
  /** Already-loaded vehicle image. Pass null for a typographic card. */
  image?: HTMLImageElement | null;
  /** Short display URL, e.g. "biltspec.com/user/slug". */
  shortUrl: string;
  /** Already-loaded QR image; drawn small in the footer when provided. */
  qrImage?: HTMLImageElement | null;
  /** Monochrome Bilt badge mark (black on transparent). */
  brandMark?: HTMLImageElement | null;
  /** Accent hex; defaults to Bilt:Spec orange. */
  accentColor?: string | null;
  dateText?: string | null;
  /** e.g. "Verified result". Only drawn when provided. */
  verifiedLabel?: string | null;
}

export const SHARE_CARD_DIMENSIONS: Record<ShareCardFormat, { width: number; height: number }> = {
  story: { width: 1080, height: 1920 },
  portrait: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
};
export function loadImageSafe(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export async function renderShareCard(options: ShareCardOptions): Promise<Blob> {
  const { captureCollectorCard } = await import('../components/passport/captureCollectorCard');
  const face = await captureCollectorCard(options.card);
  const { width, height } = SHARE_CARD_DIMENSIONS[options.format];
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Image export is unavailable');
  ctx.fillStyle = '#101215';
  ctx.fillRect(0, 0, width, height);
  const notes = (options.highlights || (options.highlight ? [options.highlight] : [])).slice(0, 3);
  const noteSpace = notes.length * 58;
  const cardHeight = Math.min(height - 230 - noteSpace, 1280),
    cardWidth = (cardHeight * 5) / 7;
  ctx.fillStyle = '#ff9a43';
  ctx.font = '600 28px sans-serif';
  ctx.fillText('BILT:SPEC  /  PASSPORT', 64, 64);
  ctx.drawImage(face, (width - cardWidth) / 2, 104, cardWidth, cardHeight);
  ctx.textAlign = 'center';
  notes.forEach((note, i) => {
    ctx.fillStyle = '#f4f3ef';
    ctx.font = '600 23px sans-serif';
    ctx.fillText(note.title, width / 2, cardHeight + 150 + i * 58, width - 128);
    if (note.detail) {
      ctx.fillStyle = '#b1b4ba';
      ctx.font = '18px sans-serif';
      ctx.fillText(note.detail, width / 2, cardHeight + 174 + i * 58, width - 128);
    }
  });
  ctx.fillStyle = '#b1b4ba';
  ctx.font = '24px sans-serif';
  ctx.fillText(options.shortUrl, width / 2, height - 60, width - 128);
  if (options.dateText) {
    ctx.font = '18px sans-serif';
    ctx.fillText(options.dateText, width / 2, height - 30, width - 128);
  }
  if (options.qrImage) {
    const size = 128;
    ctx.fillStyle = '#fff';
    ctx.fillRect(width - size - 28, 24, size + 8, size + 8);
    ctx.drawImage(options.qrImage, width - size - 24, 28, size, size);
  }
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Image export failed'))),
      'image/png'
    )
  );
}
